import React, { useEffect, useState } from 'react'
import {
  createFieldThread,
  fetchCompanyLines,
  patchInternalMember,
  addExternalMember,
  type CompanyLine,
} from '../../api/customerBridge'
import { usePermissions } from '../../hooks/usePermissions'

interface Props {
  jobId: string
  customerId?: string | null
}

/** Office/manager UI to open a field-line thread and patch in a rep. */
export function PatchInPanel({ jobId, customerId }: Props) {
  const { can } = usePermissions()
  const [lines, setLines] = useState<CompanyLine[]>([])
  const [userId, setUserId] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!can('communication.bridge')) return
    fetchCompanyLines()
      .then(setLines)
      .catch(() => setLines([]))
  }, [can])

  if (!can('communication.bridge')) return null

  const fieldLine = lines.find((l) => l.code === 'field')

  const start = async () => {
    if (!fieldLine || !userId.trim()) {
      setMessage('Pick a field line and enter the rep user id.')
      return
    }
    setBusy(true)
    setMessage(null)
    try {
      const threadId = await createFieldThread({
        company_line_id: fieldLine.id,
        scope_type: 'job',
        job_id: jobId,
        customer_id: customerId || null,
        title: `Job patch-in ${jobId.slice(0, 8)}`,
      })
      await patchInternalMember({
        thread_id: threadId,
        user_id: userId.trim(),
        is_permanent: false,
      })
      if (contactName.trim()) {
        await addExternalMember({
          thread_id: threadId,
          contact_name: contactName.trim(),
          contact_phone: contactPhone.trim() || null,
          channel: 'sms',
        })
      }
      setMessage(`Field thread started (${threadId.slice(0, 8)}…). Rep sees messages from now forward only.`)
    } catch (e) {
      setMessage((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Patch in field rep (Field line)</h3>
        <p className="text-xs text-gray-500 mt-1">
          Uses the Field company line only — never the Office/billing line. Rep history starts at
          join time.
        </p>
      </div>
      {!fieldLine && (
        <p className="text-xs text-amber-700">
          Field line not loaded. Run the foundation migration and set phone numbers on company_lines.
        </p>
      )}
      <input
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        placeholder="Rep user profile UUID"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      />
      <input
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        placeholder="Customer contact name (optional)"
        value={contactName}
        onChange={(e) => setContactName(e.target.value)}
      />
      <input
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        placeholder="Customer phone (optional)"
        value={contactPhone}
        onChange={(e) => setContactPhone(e.target.value)}
      />
      <button
        type="button"
        disabled={busy || !fieldLine}
        onClick={start}
        className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
      >
        {busy ? 'Starting…' : 'Start patch-in'}
      </button>
      {message && <p className="text-xs text-gray-600">{message}</p>}
    </div>
  )
}
